import React from 'react';
import { ExclamationTriangleIcon, InformationCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { Modal } from './Modal';
import { Body } from './Typography';

export interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info' | 'success';
  loading?: boolean;
  disabled?: boolean;
}

const variantConfig = {
  danger: {
    icon: ExclamationTriangleIcon,
    iconColor: 'text-red-500',
    iconBg: 'bg-red-100',
    confirmVariant: 'danger' as const,
  },
  warning: {
    icon: ExclamationTriangleIcon,
    iconColor: 'text-yellow-500',
    iconBg: 'bg-yellow-100',
    confirmVariant: 'primary' as const,
  },
  info: {
    icon: InformationCircleIcon,
    iconColor: 'text-blue-500',
    iconBg: 'bg-blue-100',
    confirmVariant: 'primary' as const,
  },
  success: {
    icon: CheckCircleIcon,
    iconColor: 'text-green-500',
    iconBg: 'bg-green-100',
    confirmVariant: 'primary' as const,
  },
};

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'info',
  loading = false,
  disabled = false,
}) => {
  const config = variantConfig[variant];
  const Icon = config.icon;

  const actions = [
    {
      label: cancelText,
      onClick: onClose,
      variant: 'secondary' as const,
      disabled: loading,
    },
    {
      label: confirmText,
      onClick: onConfirm,
      variant: config.confirmVariant,
      loading,
      disabled,
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      actions={actions}
      closeOnOverlayClick={!loading}
      closeOnEscape={!loading}
    >
      <div className="flex items-start space-x-4">
        <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${config.iconBg}`}>
          <Icon className={`w-6 h-6 ${config.iconColor}`} />
        </div>
        <div className="flex-1">
          <Body className="text-gray-900">
            {message}
          </Body>
        </div>
      </div>
    </Modal>
  );
};

export { ConfirmDialog };
export default ConfirmDialog;
